<template>
    <div class="menu-item-wrapper">
        <!-- 菜单头部 - 新增点击事件触发折叠/展开 -->
        <div
                class="menu-header"
                @click="toggleSubMenu"
        >
            <span class="menu-icon">
                <!-- 1. URL图标：直接显示图片 -->
                <img v-if="props.menu.menuIcon?.includes('http')" :src="props.menu.menuIcon" alt="图标" class="icon-img">
                <!-- 2. 非URL（如Document/Folder/User）：直接渲染对应图标组件 -->
                <el-icon v-else-if="props.menu.menuIcon" :class="props.menu.menuIcon">
                    <component :is="props.menu.menuIcon" />
                </el-icon>
            </span>
            <!-- 优化：折叠/展开箭头图标 -->
            <span class="menu-name">{{ props.menu.menuName }}
                <el-icon v-if="props.menu.children?.length > 0" class="collapse-icon" :class="{ 'expanded': isOpen }">
                    <CaretTop />
                </el-icon>
            </span>
            <el-tag :type="props.menu.menuType === '1' ? 'success' : 'primary'" size="small">
                {{ props.menu.menuType === '2' ? '菜单' : '文件夹' }}
            </el-tag>
            <el-tag :type="props.menu.isShow === 0 ? 'success' : 'danger'" size="small">
                {{ props.menu.isShow === 0 ? '显示' : '隐藏' }}
            </el-tag>
            <!-- 新增：菜单序号展示 -->
            <el-tag type="info" size="small">
                序号：{{ props.menu.sortNum || 0 }}
            </el-tag>
            <div class="menu-actions">
                <!-- 仅文件夹显示添加子菜单 -->
                <el-button
                        type="success"
                        size="small"
                        icon="Plus"
                        @click.stop="props.openAddSubMenuModal(props.menu.menuId)"
                        v-if="props.menu.menuType === '1'"
                        class="submenu-btn"
                >
                    添加菜单
                </el-button>
                <el-button
                        type="primary"
                        size="small"
                        icon="Edit"
                        @click.stop="props.editMenu(props.menu)"
                        class="edit-btn"
                >
                    编辑
                </el-button>
                <el-button
                        type="danger"
                        size="small"
                        icon="Delete"
                        @click.stop="props.markMenuDelete(props.menu)"
                        class="delete-btn"
                >
                    删除
                </el-button>
            </div>
        </div>

        <!-- 递归渲染子菜单 - 新增v-show控制显示/隐藏 + ref用于拖拽初始化 -->
        <div v-if="true" v-show="isOpen" class="submenu-list" ref="submenuListRef" :data-menu-id="props.menu.menuId" :data-menu-type="props.menu.menuType">
            <EditMenuModel
                    v-for="subMenu in props.menu.children"
                    :key="subMenu.menuId"
                    :menu="subMenu"
                    :checkMenuModified="menu.menuType == '1' && menu.children?.length > 0"
                    :openAddSubMenuModal="props.openAddSubMenuModal"
                    :editMenu="props.editMenu"
                    :markMenuDelete="props.markMenuDelete"
                    :handleMenuDrag="props.handleMenuDrag"
            />
        </div>
    </div>
</template>

<script setup>
    import { defineProps, ref, onMounted, nextTick } from 'vue';
    import Sortable from 'sortablejs'; // 引入Sortable
    // import { ElMessage } from 'element-plus';

    // 纯JS Props定义（保留所有必填项和默认值，新增handleMenuDrag）
    const props = defineProps({
        menu: {
            type: Object,
            required: true,
            default: () => ({
                menuId: '',
                menuName: '',
                menuIcon: '',
                menuType: '1',
                menuUrl: '',
                isShow: 0,
                sortNum: 0,
                parentMenuId: '',
                children: []
            })
        },
        openAddSubMenuModal: {
            type: Function,
            required: true // 保留添加子菜单方法
        },
        editMenu: {
            type: Function,
            required: true // 保留编辑菜单方法
        },
        markMenuDelete: {
            type: Function,
            required: true // 保留删除菜单方法
        },
        handleMenuDrag: {
            type: Function,
            required: true // 拖拽处理方法
        }
    });

    // 新增：控制子菜单展开/收起的状态，默认展开
    const isOpen = ref(true);
    // ========== 拖拽板块 ==========
    // 子菜单列表ref，用于初始化Sortable
    const submenuListRef = ref(null);
    // 存储Sortable实例，避免重复初始化
    let sortableInstance = null;

    // 初始化Sortable（子菜单的拖拽渲染 关联 submenuListRef）
    const initSortable = () => {
        if (!submenuListRef.value) return;

        // 销毁原有实例，避免重复
        if (sortableInstance) {
            sortableInstance.destroy();
        }

        // 初始化Sortable
        sortableInstance = new Sortable(submenuListRef.value, {
            animation: 150, // 拖拽动画时长
            ghostClass: 'sortable-ghost', // 拖拽占位样式（可自定义）
            handle: '.menu-header', // 仅允许点击菜单头部拖拽 避免点击「编辑 / 删除」按钮触发
            filter: '.menu-actions', //排除 .menu-actions（操作按钮区） 避免点击「编辑 / 删除」按钮触发
            draggable: '.menu-item-wrapper', // 指定可拖拽的元素
            group: {
                name: 'menu-group', // 统一拖拽组名，支持跨层级
                pull: true,      // 允许拖出
                put: true           // 允许拖入
            },
            // 拖拽过程中的校验 可阻止拖拽
            onMove: (evt) => {
                // 获取目标父菜单类型
                const newmenuType = evt.to?.dataset.menuType //对应 data-menu-type
                // 顶级菜单容器无data-menu-id，允许拖拽（顶级默认是文件夹）
                if (!newmenuType) return false;
                // 校验：目标父菜单必须是文件夹（menuType=1）
                if (newmenuType !== '1'){
                    return false;
                }
                return true; // 允许拖拽
            },
            // 拖拽后的校验 无法阻止拖拽
            // onAdd: (evt) => {
            //     const targetParentMenu = props.menu;
            //     if (targetParentMenu.menuType !== '1') {
            //         evt.preventDefault(); // 阻止拖入非文件夹
            //         ElMessage.warning('只能拖拽到文件夹类型的菜单下');
            //     }
            // },
            // 拖拽结束事件
            onEnd: (evt) => {
                const menuId = props.menu.children[evt.oldIndex].menuId; // 被拖拽的菜单ID
                const oldParentMenuId = props.menu.menuId; //当前容器的菜单ID（文件夹ID）
                const newParentMenuId = evt.to?.dataset.menuId || null;//目标菜单ID 需要给每个子容器设置data-menu-id才被能取到
                const newIndex = evt.newIndex; // 新索引

                // 调用拖拽处理方法
                props.handleMenuDrag({
                    menuId,
                    oldParentMenuId,
                    newParentMenuId,
                    newIndex
                });

            }

        });
    };

    // ========== 拖拽板块结束 ==========


    // 新增：切换子菜单展开/收起的方法
    const toggleSubMenu = () => {
        // 只有存在子菜单时才触发切换
        if (props.menu.children?.length) {
            isOpen.value = !isOpen.value;
            // 切换后重新初始化Sortable（展开时）
            if (isOpen.value) {
                nextTick(initSortable);
            }
        }
    };

    // 渲染Sortable
    //因为递归页再取到菜单后才渲染，所以此时渲染拖拽模组等待页面渲染完毕就行
    onMounted(() => {
        nextTick(initSortable);
    });

</script>

<style scoped>
    /* 保留所有原有样式，优化折叠箭头相关样式 */
    .menu-item-wrapper {
        display: block;
        width: 100%;
        cursor: move; /* 拖拽光标提示 */
    }

    .submenu-list {
        margin-left: 24px;
        padding-left: 12px;
        border-left: 2px solid #f0f0f0;
    }

    /* 深度选择器保留，确保Element按钮样式生效 */
    :deep(.submenu-btn) {
        color: #67c23a !important;
        background: #f0f9ff !important;
        border: none !important;
    }

    :deep(.edit-btn) {
        color: #409eff !important;
        background: #ecf5ff !important;
        border: none !important;
    }

    :deep(.delete-btn) {
        color: #f56c6c !important;
        background: #fef0f0 !important;
        border: none !important;
    }

    /* 优化：折叠箭头样式（核心修改） */
    .collapse-icon {
        margin: 0 6px;
        font-size: 14px; /* 适配常规文字大小 */
        color: #666; /* 中性色，不刺眼 */
        transition: transform 0.2s ease-in-out; /* 更顺滑的过渡 */
        cursor: pointer;
        flex-shrink: 0; /* 防止被挤压变形 */
        transform: rotate(90deg);/* 初始角度 */
        vertical-align: middle; /* 对齐文字中线 */
        margin-top: -3px; /* 正数向下，负数向上*/
    }

    /* 展开状态下旋转90度 */
    .collapse-icon.expanded {
        transform: rotate(180deg);
    }

    /* 鼠标悬浮时变色，提升交互反馈 */
    .collapse-icon:hover {
        color: #409eff; /* 主色调，和编辑按钮呼应 */
    }

    .menu-header {
        cursor: pointer;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        padding: 8px 0;
        line-height: 20px; /* 统一行高，对齐更美观 */
    }

    /* 优化菜单名称样式，和箭头对齐更协调 */
    .menu-name {
        flex-shrink: 0;
        font-size: 14px;
    }

    /* 图标容器样式优化，防止错位 */
    .menu-icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
    }

    .icon-img {
        width: 16px;
        height: 16px;
        object-fit: contain;
    }

    /* ========== 拖拽板块 ========== */
    /* Sortable拖拽占位样式（保持原有布局） */
    :deep(.sortable-ghost) {
        opacity: 0.8;
        background: #f5f7fa;
        border-radius: 4px;
    }
    /* 拖拽时按钮区域禁止触发拖拽 */
    :deep(.menu-actions) {
        pointer-events: auto;
    }
    /* ========== 拖拽板块结束 ========== */
</style>